import React, { useState } from 'react';
import { translations } from '../translations';
import { useToast } from '../hooks/use-toast';
import { Save, Globe } from 'lucide-react';

const AdminContent = () => {
  const [selectedLang, setSelectedLang] = useState('es');
  const [content, setContent] = useState(translations);
  const [hasChanges, setHasChanges] = useState(false);
  const { toast } = useToast();

  const languages = [
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'zh', name: '中文', flag: '🇨🇳' }
  ];

  const handleChange = (section, key, value) => {
    const newContent = { ...content };
    newContent[selectedLang][section][key] = value;
    setContent(newContent);
    setHasChanges(true);
  };

  const handleSave = () => {
    // Save to localStorage for now
    localStorage.setItem('hexaparks_content', JSON.stringify(content));
    setHasChanges(false);
    toast({
      title: 'Success',
      description: 'Content saved successfully'
    });
  };

  const renderSection = (sectionKey, sectionData) => {
    if (typeof sectionData !== 'object') return null;

    return (
      <div key={sectionKey} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 capitalize">
          {sectionKey.replace(/_/g, ' ')}
        </h3>
        <div className="space-y-4">
          {Object.entries(sectionData).map(([key, value]) => {
            if (typeof value === 'string') {
              return (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                    {key.replace(/_/g, ' ')}
                  </label>
                  {value.length > 100 ? (
                    <textarea
                      value={value}
                      onChange={(e) => handleChange(sectionKey, key, e.target.value)}
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                    />
                  ) : (
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleChange(sectionKey, key, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                    />
                  )}
                </div>
              );
            } else if (Array.isArray(value)) {
              return (
                <div key={key} className="border-l-4 border-amber-500 pl-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                    {key.replace(/_/g, ' ')} (Array - {value.length} items)
                  </label>
                  <p className="text-xs text-gray-500">
                    Complex array editing coming soon. Use the translations.js file for now.
                  </p>
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Content Management</h2>
          <p className="text-gray-600 mt-2">Edit website content for all languages</p>
        </div>
        {hasChanges && (
          <button
            onClick={handleSave}
            className="flex items-center gap-2 bg-amber-600 text-white px-6 py-3 rounded-lg hover:bg-amber-700 transition-colors"
          >
            <Save className="w-5 h-5" />
            Save Changes
          </button>
        )}
      </div>

      <div className="mb-6 flex gap-3">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => setSelectedLang(lang.code)}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
              selectedLang === lang.code
                ? 'bg-amber-600 text-white shadow-lg'
                : 'bg-white text-gray-700 border border-gray-200 hover:border-amber-600'
            }`}
          >
            <span className="text-2xl">{lang.flag}</span>
            <span>{lang.name}</span>
          </button>
        ))}
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Changes are saved locally for preview. To persist changes permanently,
          you need to update the translations.js file or implement backend content storage.
        </p>
      </div>

      {content[selectedLang] &&
        Object.entries(content[selectedLang]).map(([sectionKey, sectionData]) =>
          renderSection(sectionKey, sectionData)
        )}
    </div>
  );
};

export default AdminContent;
